import React from 'react';
import PropTypes from 'prop-types';

import {Paper, Stack, Typography, Box, Button} from 'views/components';
import {Translate, translate, useQuery} from 'views/module';
import {ArrowBack, ArrowForward, Save} from 'views/icons';

import ProgressIndicator from './components/ProgressIndicator';
import {WelcomeSection, ContactSection, InterviewSection, ResumeSection} from './sections';
import {formatJobScheduleStrings} from '../job_board/sections/Body/util';

import backend from 'utils/backend';

const CaregiverInterview = (props) => {
  const {jobRn} = props;
  // Default caregiver data structure
  const defaultCaregiverData = {
    jobRn: jobRn,
    contact: {},
    interviewQuestions: {
      certifications: {
        required: 'none',
        atLeastOne: 'none',
      },
      languages: 'none',
      considerations: 'none',
      hourlyRate: 'none',
      startDate: 'none',
    },
    specificNeeds: 'none',
    shifts: 'none',
  };

  const [caregiverData, setCaregiverData] = React.useState(() => defaultCaregiverData);
  const [currentSectionIndex, setCurrentSectionIndex] = React.useState(0);
  const [showSaved, setShowSaved] = React.useState(false);

  // Resume data state - load from localStorage
  const [caregiverResumeData, setCaregiverResumeData] = React.useState(() => {
    const saved = localStorage.getItem('CaregiverInterview-ResumeData');
    return saved ? JSON.parse(saved) : null;
  });

  const query = useQuery();

  // Pre-populate contact data from resume if available
  React.useEffect(() => {
    if (caregiverResumeData?.caregiverRn && caregiverResumeData?.resume) {
      const resume = caregiverResumeData.resume;
      const contactData = {
        firstName: resume.firstName || '',
        lastName: resume.lastName || '',
        email: resume.email || '',
        phone: resume.phoneNumber || '',
        streetAddress: resume.streetAddress || '',
        city: resume.city || '',
        province: resume.province || '',
        postalCode: resume.postalCode || '',
      };

      setCaregiverData((prev) => ({
        ...prev,
        contact: contactData,
      }));
    }
  }, [caregiverResumeData]);

  // Scroll the main Stack to top when currentSectionIndex changes
  const stackRef = React.useRef(null);
  React.useEffect(() => {
    if (stackRef.current) {
      stackRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }, [currentSectionIndex]);
  React.useEffect(() => {
    if (!jobRn) {
      // Set default caregiver data if no jobRn is provided
      setCaregiverData((prev) => ({
        ...prev,
        jobRn: null,
        interviewQuestions: {
          certifications: {
            required: 'none',
            atLeastOne: 'none',
          },
          languages: 'none',
          considerations: 'none',
          hourlyRate: 'none',
          startDate: 'none',
        },
        specificNeeds: 'none',
        shifts: 'none',
      }));
      return;
    }

    const fetchJobData = async () => {
      try {
        const job = await backend.career.getJob(jobRn);
        const jobData = job.job;
        if (!jobData) return;

        const priorities = jobData.priorities || [];
        const requirements = jobData.requirements || {};
        const price = jobData.price || {};
        const startDate = jobData.startDate || {};

        const formattedQuestions = {
          certifications: {
            required: 'none',
            atLeastOne: 'none',
          },
          languages: 'none',
          considerations: 'none',
          hourlyRate: 'none',
          startDate: 'none',
        };

        priorities.slice(0, 3).forEach((priority) => {
          switch (priority) {
            case 'HOURLY_RATE':
              formattedQuestions.hourlyRate = price.hourlyRate?.toString() || 'none';
              break;
            case 'CERTIFICATIONS':
              const requiredCerts = requirements.certifications || [];
              const atLeastOneCerts = requirements.atLeastOneCertifications || [];
              formattedQuestions.certifications = {
                required: requiredCerts.length > 0 ? requiredCerts.join(', ') : 'none',
                atLeastOne: atLeastOneCerts.length > 0 ? atLeastOneCerts.join(', ') : 'none',
              };
              break;
            case 'START_DATE':
              formattedQuestions.startDate = startDate.startTime || 'none';
              break;
            case 'CONSIDERATIONS':
              const consParts = [
                ...(requirements.considerations || []),
                ...(requirements.additionalConsiderations?.blob ? [requirements.additionalConsiderations.blob] : []),
              ];
              formattedQuestions.considerations = consParts.length > 0 ? `[${consParts.join(', ')}]` : 'none';
              break;
            case 'LANGUAGES':
              const languages = (requirements.languages || []).filter((lang) => lang.toUpperCase() !== 'ENGLISH');
              formattedQuestions.languages = languages.length > 0 ? `[${languages.join(', ')}]` : 'none';
              break;
          }
        });

        const shifts = formatJobScheduleStrings(jobData.schedule);

        setCaregiverData((prev) => ({
          ...prev,
          jobRn,
          interviewQuestions: formattedQuestions,
          specificNeeds: requirements.careNeeds?.length > 0 ? `[${requirements.careNeeds.join(', ')}]` : 'none',
          shifts: shifts.length > 0 ? `[${shifts.join(', ')}]` : 'none',
        }));
      } catch (error) {
        console.error('Failed to fetch job data:', error);
      }
    };

    fetchJobData();
  }, [query, jobRn]);

  // Track validity of each section
  const [sectionValidity, setSectionValidity] = React.useState({
    resume: true,
    contact: false,
  });

  // Track processing state for resume section
  const [isResumeProcessing, setIsResumeProcessing] = React.useState(false);

  // Generic handlers that work for any section
  const handleDataChange = React.useCallback((section, data) => {
    setCaregiverData((prevData) => ({...prevData, [section]: data}));
  }, []);

  const handleValidityChange = React.useCallback((section, isValid) => {
    setSectionValidity((prev) => ({...prev, [section]: isValid}));
  }, []);

  // Individual handlers using the generic functions
  const handleContactChange = React.useCallback((data) => handleDataChange('contact', data), [handleDataChange]);

  const handleContactValidity = React.useCallback((isValid) => handleValidityChange('contact', isValid), [handleValidityChange]);

  const handleResumeValidity = React.useCallback((isValid) => handleValidityChange('resume', isValid), [handleValidityChange]);

  const handleResumeDataChange = React.useCallback((data) => {
    setCaregiverResumeData(data);
    localStorage.setItem('CaregiverInterview-ResumeData', JSON.stringify(data));
  }, []);

  // Merge step labels and sections into a single array
  const steps = [
    {
      key: 'welcome',
      label: translate('caregiverInterview.Steps.Welcome'),
      Component: <WelcomeSection key="welcome" />,
    },
    {
      key: 'resume',
      label: translate('caregiverInterview.Steps.Resume'),
      Component: (
        <ResumeSection
          key="resume"
          caregiverData={caregiverData}
          caregiverResumeData={caregiverResumeData}
          onResumeDataChange={handleResumeDataChange}
          setValidity={handleResumeValidity}
          setIsProcessing={setIsResumeProcessing}
        />
      ),
    },
    {
      key: 'contact',
      label: translate('caregiverInterview.Steps.Contact'),
      Component: (
        <ContactSection
          key="contact"
          contactData={caregiverData.contact}
          onContactChange={handleContactChange}
          setValidity={handleContactValidity}
        />
      ),
    },
    {
      key: 'interview',
      label: translate('caregiverInterview.Steps.Interview'),
      Component: <InterviewSection key="interview" caregiverData={caregiverData} />,
    },
  ];

  const handleNextSection = () => {
    if (currentSectionIndex < steps.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      handleSave(currentSectionIndex + 1);
    }
  };

  const handlePreviousSection = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
      handleSave(currentSectionIndex - 1);
    }
  };

  const handleSave = (curSection=null, data=null) => {
    localStorage.setItem('CaregiverInterview-CaregiverData', JSON.stringify(data ?? caregiverData));
    localStorage.setItem('CaregiverInterview-SectionValidity', JSON.stringify(sectionValidity));

    setShowSaved(true);
    setTimeout(() => {
      setShowSaved(false);
    }, 2000); // Show "Saved" for 2 seconds
  };

  return (
    <Stack ref={stackRef} className="caregiver-screener" sx={{background: 'linear-gradient(180deg,rgba(252, 246, 239, 1) 0%, rgba(255, 255, 255, 1) 100%)'}}>
      <Paper square sx={{paddingBlock: 2, paddingInline: 8, display: 'flex', justifyContent: 'center'}}>
        <Box sx={{width: {xs: '100%', sm: '100%', md: '100%', lg: '100%', xl: 1400}}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap'}}>
            <Stack
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto',
                  md: 'auto',
                  lg: 'auto',
                  xl: 'auto',
                },
                textAlign: {
                  xs: 'center',
                  sm: 'start',
                  md: 'start',
                  lg: 'start',
                  xl: 'start',
                },
              }}
            >
              <Typography variant='h4' fontWeight={600}>
                <Translate expression='caregiverInterview.Main.Title'/>
              </Typography>
              <Typography variant='subtitle1' color='text.secondary'>
                <Translate expression='caregiverInterview.Main.Subtitle'/>
              </Typography>
            </Stack>
            <Box sx={{display: {xs: 'none', sm: 'flex', md: 'flex', lg: 'flex', xl: 'flex'}, alignItems: 'center'}}>
              <Button startIcon={<Save />} disableElevation onClick={handleSave} >
                <Translate expression={showSaved ? 'caregiverInterview.Main.Saved' : 'caregiverInterview.Main.Save'}/>
              </Button>
            </Box>
          </Box>
          <ProgressIndicator
            totalSteps={steps.length}
            curStep={currentSectionIndex + 1}
            stepLabels={steps.map((s) => s.label)}
          />
          <Box sx={{mt: 2, display: {xs: 'flex', sm: 'none', md: 'none', lg: 'none', xl: 'none'}, alignItems: 'center', justifyContent: 'center'}}>
            <Button startIcon={<Save />} disableElevation onClick={handleSave} >
              <Translate expression={showSaved ? 'caregiverInterview.Main.Saved' : 'caregiverInterview.Main.Save'}/>
            </Button>
          </Box>
        </Box>
      </Paper>
      <Box
        sx={{
          my: 4,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            width: {
              xs: '100%',
              sm: '95%',
              md: 850,
              lg: 1000,
            },
          }}
        >
          {/* Render the current step's component */}
          {steps[currentSectionIndex]?.Component}
          <Box
            sx={{
              p: 3,
              backgroundColor: '#F8F8F8',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              disableElevation
              onClick={handlePreviousSection}
              disabled={currentSectionIndex === 0 || isResumeProcessing}
            >
              <Translate expression='caregiverInterview.Main.Back'/>
            </Button>
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForward />}
              disableElevation
              onClick={handleNextSection}
              disabled={
                currentSectionIndex === steps.length - 1 ||
                (currentSectionIndex !== 0 && !sectionValidity[steps[currentSectionIndex].key]) ||
                isResumeProcessing
              }
            >
              <Translate expression={
                steps[currentSectionIndex].key === 'resume' && !caregiverResumeData?.caregiverRn ?
                  'caregiverInterview.Main.Skip' :
                  'caregiverInterview.Main.Next'
              }/>
            </Button>
          </Box>
        </Paper>
      </Box>
    </Stack>
  );
};

CaregiverInterview.propTypes = {
  jobRn: PropTypes.string,
};

export default React.memo(CaregiverInterview);
